import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaSeedling, FaLayerGroup, FaProjectDiagram, FaMedkit, FaLeaf, FaAppleAlt, FaTree, FaVial, FaBoxOpen, FaPills, FaCoffee
} from 'react-icons/fa';
import { ArrowRight } from 'lucide-react';
import './HomeProducts.css';

const categories = [
  { id: 1, icon: <FaSeedling />, label: 'Field Crops', description: 'Enhance your yield with our premium field crop solutions, designed to boost productivity and resistance against common pests and diseases.', image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?q=80&w=600&h=450&auto=format&fit=crop' },
  { id: 2, icon: <FaLayerGroup />, label: 'Multi Cropping Project (MCP)', description: 'Optimize your land usage and maximize returns with our integrated multi-cropping project strategies.', image: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?q=80&w=600&h=450&auto=format&fit=crop' }, /* Soil/Farmland image */
  { id: 3, icon: <FaProjectDiagram />, label: 'Multi Cropping Farmland Project (MCFP)', description: 'Sustainable farmland projects designed to incorporate multi-cropping practices for long term fertility and yield.', image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?q=80&w=600&h=450&auto=format&fit=crop' },
  { id: 4, icon: <FaMedkit />, label: 'Medical Plants', description: 'Cultivation of high-grade medicinal plants for the pharmaceutical and wellness industries.', image: 'https://res.cloudinary.com/drevfgyks/image/upload/v1725105498/AMARAVATHI%20NATURO%20TECH%20PVT%20LTD/7_vndxzw.png' }, /* Cloudinary Medicinal Crop */
  { id: 5, icon: <FaLeaf />, label: 'Aromatics Plants', description: 'Support the growth of top-quality aromatic plants used for essential oils and fragrances.', image: 'https://res.cloudinary.com/drevfgyks/image/upload/v1725105495/AMARAVATHI%20NATURO%20TECH%20PVT%20LTD/2_pnju9o.png' }, /* Cloudinary Aromatics/Oils Crop */
  { id: 6, icon: <FaAppleAlt />, label: 'Exotic Fruits', description: 'Grow high-value, exotic fruits tailored for global markets with our premium agricultural inputs.', image: 'https://res.cloudinary.com/dexueorjm/image/upload/v1776843432/avocado_and_macadamia-2_twdsid.png' }, /* Avocado */
  { id: 7, icon: <FaPills />, label: 'Spices', description: 'Unlock the true aroma and flavor of spices with our specialized farming inputs.', image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?q=80&w=600&h=450&auto=format&fit=crop' },
  { id: 8, icon: <FaVial />, label: 'Natural Ingredients', description: 'Raw, unadulterated natural ingredients naturally sourced for food, cosmetic, and healthcare brands.', image: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?q=80&w=600&h=450&auto=format&fit=crop' }, /* Visible herbs/spices */
  { id: 9, icon: <FaBoxOpen />, label: 'Natural Food Supplement', description: 'Premium agricultural support for crops destined to become natural health supplements.', image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?q=80&w=600&h=450&auto=format&fit=crop' }, /* Healthy food spread */
  { id: 10, icon: <FaTree />, label: 'Forestry Wood Bearings', description: 'Premium forestry solutions and branding for specialized wood products, ensuring sustainable growth.', image: 'https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?q=80&w=600&h=450&auto=format&fit=crop' }, /* Strong forest tree logs - squared */
  // { id: 11, icon: <FaCoffee />, label: 'Plantation', description: 'Sustainable solutions for large-scale plantation crops like coffee, tea, and coco.', image: 'https://images.unsplash.com/photo-1524350876685-274059332603?q=80&w=600&h=450&auto=format&fit=crop' }
];

const HomeProducts = () => {
  const [activeTab, setActiveTab] = useState(categories[0].id);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Simple reveal on load
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const activeCategory = categories.find(cat => cat.id === activeTab);

  return (
    <section className={`home-products-section ${isVisible ? 'visible' : ''}`}>
      <div className="home-products-container">

        {/* Header */}
        <div className="section-header">
          <span className="subtitle">Our Offerings</span>
          <h2 className="title">Premium <span className="highlight">Products</span></h2>
          <p className="description">
            Explore our diverse range of agricultural solutions tailored to meet every farming need.
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="product-navigation">
          <div className="nav-track">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`nav-pill ${activeTab === cat.id ? 'active' : ''}`}
                onClick={() => setActiveTab(cat.id)}
              >
                <span className="nav-icon">{cat.icon}</span>
                <span className="nav-label">{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Featured Product Display */}
        <div className="product-display-area">
          <div className="product-card" key={activeTab}> {/* Key helps trigger animation on change */}
            <div className="card-image-wrapper">
              <img
                src={activeCategory.image}
                alt={activeCategory.label}
                className="card-image"
              />
              <div className="image-overlay"></div>
            </div>

            <div className="card-content">
              <div className="content-inner">
                <div className="icon-badge">
                  {activeCategory.icon}
                </div>
                <h3 className="card-title">{activeCategory.label}</h3>
                <p className="card-description">{activeCategory.description}</p>

                <Link to="/crops-products" className="learn-more-link">
                  <span className="btn-text">Explore Solutions</span>
                  <div className="btn-icon">
                    <ArrowRight size={18} />
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default HomeProducts;
